import React, { useEffect, useMemo, useRef, useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'

import { type Assignment } from '@lyrahealth-inc/shared-app-logic/src/models/assignments/Assignments'

import { AssessmentModalForm } from './AssessmentModalForm'
import {
  ACTIVE_STATUS,
  ASSESSMENT_MODAL_MESSAGES,
  type AssessmentFrequency,
  getEditAssessmentBanner,
  getFrequencyOptions,
  getLatestFlexibleAssignment,
  isPendingAssignment,
  PAUSED_STATUS,
} from './assessmentModalHelpers'
import {
  type FlexibleAssessment,
  ONE_OFF_FREQUENCY,
  useUpdateFlexibleAssessmentMutation,
} from './data/flexibleAssessmentsApi'
import { useAppDispatch } from '../../data/store'
import { setToastContent } from '../data/ltToastAutoActions'

export type EditAssessmentModalProps = {
  visible: boolean
  flexibleAssessment: FlexibleAssessment
  assessmentName: string
  nextSessionDate?: Date
  assignments: readonly Assignment[]
  isAssignmentsLoading: boolean
  hasAssignmentsError: boolean
  onRequestClose: () => void
}

const MESSAGES = defineMessages({
  frequencyPausedAccessibilityLabel: {
    defaultMessage: 'Frequency. Paused assessments cannot be edited',
    description: 'Accessibility label for the frequency selector when a paused assessment cannot be edited',
  },
  modalTitle: {
    defaultMessage: 'Edit assessment',
    description: 'Title and accessibility label for the edit assessment modal',
  },
  assessmentUpdated: {
    defaultMessage: '{assessmentName} updated',
    description: 'Success toast after updating a flexible assessment',
  },
  assessmentUpdateFailed: {
    defaultMessage: "{assessmentName} couldn't be updated. Please try again.",
    description: 'Error shown in the edit assessment modal when updating a flexible assessment fails',
  },
  sendEmail: {
    defaultMessage: 'Send email',
    description: 'Button to send a one-time assessment email',
  },
  confirm: {
    defaultMessage: 'Confirm',
    description: 'Button to confirm a recurring assessment',
  },
})

export const EditAssessmentModal: React.FC<EditAssessmentModalProps> = ({
  visible,
  flexibleAssessment,
  assessmentName,
  nextSessionDate,
  assignments,
  isAssignmentsLoading,
  hasAssignmentsError,
  onRequestClose,
}) => {
  const intl = useIntl()
  const { formatMessage } = intl
  const dispatch = useAppDispatch()
  const [updateFlexibleAssessment, { isLoading }] = useUpdateFlexibleAssessmentMutation()
  const submissionInFlight = useRef(false)
  const [frequency, setFrequency] = useState<AssessmentFrequency | ''>('')
  const [submitError, setSubmitError] = useState<string>()

  useEffect(() => {
    if (visible) {
      setFrequency(
        Number.isInteger(flexibleAssessment.frequency) && flexibleAssessment.frequency >= ONE_OFF_FREQUENCY
          ? flexibleAssessment.frequency
          : '',
      )
      setSubmitError(undefined)
      submissionInFlight.current = false
    }
  }, [flexibleAssessment, visible])

  const assessmentOptions = useMemo(
    () => [{ label: assessmentName, value: flexibleAssessment.content_meta_data_id }],
    [assessmentName, flexibleAssessment.content_meta_data_id],
  )
  const frequencyOptions = useMemo(() => {
    const options = getFrequencyOptions(formatMessage)
    const originalFrequency = flexibleAssessment.frequency

    if (
      Number.isInteger(originalFrequency) &&
      originalFrequency >= ONE_OFF_FREQUENCY &&
      !options.some(({ value }) => value === originalFrequency)
    ) {
      options.push({
        label: formatMessage(ASSESSMENT_MODAL_MESSAGES.customRecurringFrequency, { frequency: originalFrequency }),
        value: originalFrequency,
      })
    }

    return options
  }, [flexibleAssessment.frequency, formatMessage])
  const isPaused = flexibleAssessment.status === PAUSED_STATUS
  const isSendOnce = frequency === ONE_OFF_FREQUENCY
  const isSelectedFrequencyAvailable = frequencyOptions.some(({ value }) => value === frequency)
  const areAssignmentsReady = !isAssignmentsLoading && !hasAssignmentsError

  const latestFlexibleAssignment = useMemo(
    () =>
      getLatestFlexibleAssignment(assignments, flexibleAssessment.episode_id, flexibleAssessment.content_meta_data_id),
    [assignments, flexibleAssessment.content_meta_data_id, flexibleAssessment.episode_id],
  )

  const canResendOneOff =
    isSendOnce && flexibleAssessment.frequency === ONE_OFF_FREQUENCY && !isPendingAssignment(latestFlexibleAssignment)
  const canSubmit =
    frequency !== '' &&
    isSelectedFrequencyAvailable &&
    areAssignmentsReady &&
    flexibleAssessment.status === ACTIVE_STATUS &&
    (frequency !== flexibleAssessment.frequency || canResendOneOff)

  const banner =
    isSelectedFrequencyAvailable && areAssignmentsReady
      ? getEditAssessmentBanner({
          intl,
          assessmentName,
          frequency,
          existingAssessment: flexibleAssessment,
          latestAssignment: latestFlexibleAssignment,
          nextSessionDate,
        })
      : undefined

  useEffect(() => {
    if (frequency !== '' && !isSelectedFrequencyAvailable) setFrequency('')
  }, [frequency, isSelectedFrequencyAvailable])

  const handleFrequencyChange = (value: AssessmentFrequency | '') => {
    setSubmitError(undefined)
    if (value === '') {
      setFrequency('')
      return
    }

    const selectedFrequency = frequencyOptions.find(({ value: optionValue }) => optionValue === Number(value))?.value
    setFrequency(selectedFrequency ?? '')
  }

  const handleRequestClose = () => {
    if (submissionInFlight.current || isLoading) return

    onRequestClose()
  }

  const handleConfirm = async () => {
    if (!canSubmit || isLoading || submissionInFlight.current) return

    submissionInFlight.current = true
    setSubmitError(undefined)

    try {
      await updateFlexibleAssessment({
        id: flexibleAssessment.id,
        frequency,
        previousFrequency: flexibleAssessment.frequency,
        previousStatus: flexibleAssessment.status,
      }).unwrap()
    } catch {
      setSubmitError(formatMessage(MESSAGES.assessmentUpdateFailed, { assessmentName }))
      return
    } finally {
      submissionInFlight.current = false
    }

    dispatch(
      setToastContent({
        text: formatMessage(MESSAGES.assessmentUpdated, { assessmentName }),
        id: 'AssessmentModal-edit-updateSuccess',
        toastType: 'success',
      }),
    )
    onRequestClose()
  }

  return (
    <AssessmentModalForm
      visible={visible}
      mode='edit'
      title={formatMessage(MESSAGES.modalTitle)}
      assessmentValue={flexibleAssessment.content_meta_data_id}
      assessmentOptions={assessmentOptions}
      assessmentDisabled
      onAssessmentChange={() => undefined}
      frequency={frequency}
      frequencyOptions={frequencyOptions}
      frequencyDisabled={isPaused}
      frequencyAccessibilityLabel={isPaused ? formatMessage(MESSAGES.frequencyPausedAccessibilityLabel) : undefined}
      onFrequencyChange={handleFrequencyChange}
      banner={banner}
      submitError={submitError}
      confirmText={formatMessage(isSendOnce ? MESSAGES.sendEmail : MESSAGES.confirm)}
      canSubmit={canSubmit}
      isLoading={isLoading}
      onConfirm={() => void handleConfirm()}
      onRequestClose={handleRequestClose}
    />
  )
}
